import React, { useEffect, useMemo, useState } from 'react';
import useRagTestRun from '../../../hooks/useRagTestRun';
import { WorkspacePanel, PANEL_WIDTH } from '../../layout/WorkspaceView';
import RunInputColumn from './RunInputColumn';
import OperationList from './OperationList';
import OperationDetail from './OperationDetail';
import TestingIntro from './TestingIntro';
import CountPill from '../../../../shared/components/pill/CountPill';

// Narrower than a standard panel: its rows are short labels with a duration, not form controls.
const OPERATIONS_WIDTH = 320;

export default function AiTestingPanel() {
  const [assetId, setAssetId] = useState(null);
  const [question, setQuestion] = useState('');
  const [mode, setMode] = useState('retrieve');
  const [selectedStepId, setSelectedStepId] = useState(null);

  const { run, loading, error, runTest, answerFromFragments } = useRagTestRun();

  const steps = useMemo(() => run?.steps || [], [run]);

  // Land on the last step of a finished run: for a full run that is the answer, which is what
  // you came to look at. Keeps a stale selection from a previous run out of the detail pane.
  useEffect(() => {
    setSelectedStepId(steps.length > 0 ? steps[steps.length - 1].id : null);
  }, [run]); // eslint-disable-line react-hooks/exhaustive-deps

  // Citations live on the answer step but are rendered against fragments owned by the retrieval
  // steps, so they are lifted here and handed down to whichever step is being viewed.
  const citedIds = useMemo(() => {
    const answer = steps.find((s) => s.type === 'answer');
    return answer?.detail?.cited_fragment_ids || [];
  }, [steps]);

  const selectedStep = steps.find((s) => s.id === selectedStepId) || null;

  // Continuing a retrieve-only run, and re-running an answer, are the same call: generate from
  // a named fragment set. The new steps are appended to the run, and the first of them is
  // selected so the result is what you are looking at when it lands.
  const handleAnswerFrom = async (fragmentIds) => {
    const firstNewStepId = await answerFromFragments({
      assetId,
      messages: [{ role: 'user', content: question }],
      fragmentIds,
    });
    if (firstNewStepId) setSelectedStepId(firstNewStepId);
  };

  return (
    <>
      <WorkspacePanel title="Run a test" width={PANEL_WIDTH}>
        <RunInputColumn
          assetId={assetId}
          onAssetChange={setAssetId}
          question={question}
          onQuestionChange={setQuestion}
          mode={mode}
          onModeChange={setMode}
          running={loading}
          onRun={() => runTest({ assetId, messages: [{ role: 'user', content: question }], mode })}
        />
      </WorkspacePanel>

      <WorkspacePanel
        title="Operations"
        width={OPERATIONS_WIDTH}
        actions={steps.length > 0 ? <CountPill>{steps.length}</CountPill> : null}
      >
        <OperationList steps={steps} selectedId={selectedStepId} onSelect={setSelectedStepId} />
      </WorkspacePanel>

      <WorkspacePanel title={selectedStep ? selectedStep.label : 'About this tool'} grow>
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}

        {selectedStep ? (
          <OperationDetail
            step={selectedStep}
            citedIds={citedIds}
            onAnswerFrom={handleAnswerFrom}
            busy={loading}
          />
        ) : (
          <TestingIntro />
        )}
      </WorkspacePanel>
    </>
  );
}
